import React, { useEffect, useState, useContext } from 'react';
import { getProjects } from '../utils/firestore';
import { SettingsContext } from '../contexts/SettingsContext';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faSpinner } from '@fortawesome/free-solid-svg-icons';

const ProjectListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-4);
  background: rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-lg);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
`;

const ProjectItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-4);
  background: ${props => props.isActive ? 'rgba(74, 144, 226, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.isActive ? 'white' : 'var(--text-color)'};
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid ${props => props.isActive ? 'rgba(74, 144, 226, 0.3)' : 'rgba(255, 255, 255, 0.2)'};
  box-shadow: ${props => props.isActive ? '0 2px 8px rgba(74, 144, 226, 0.2)' : '0 1px 4px rgba(0, 0, 0, 0.05)'};
  font-family: var(--font-heading-regular);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);

  &:hover {
    background: ${props => props.isActive ? 'rgba(74, 144, 226, 0.4)' : 'rgba(255, 255, 255, 0.15)'};
    border-color: ${props => props.isActive ? 'rgba(74, 144, 226, 0.4)' : 'rgba(74, 144, 226, 0.2)'};
    transform: translateY(-1px);
    box-shadow: ${props => props.isActive ? '0 4px 12px rgba(74, 144, 226, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)'};
  }
`;

const ProjectName = styled.span`
  font-weight: 400;
  font-size: var(--text-base);
  font-family: var(--font-heading-medium);
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: var(--space-1);
  margin-left: var(--space-2);
  opacity: 0.7;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: var(--radius-sm);

  &:hover {
    opacity: 1;
    background-color: rgba(255, 255, 255, 0.1);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }
`;

const NewProjectButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: var(--space-3) var(--space-4);
  background: linear-gradient(135deg, var(--primary-600) 0%, var(--primary-700) 100%);
  color: white;
  border: none;
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: var(--font-heading-regular);
  font-size: var(--text-base);
  font-weight: 300;
  box-shadow: 0 2px 8px rgba(90, 99, 230, 0.2);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 50%);
    border-radius: inherit;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    background: linear-gradient(135deg, var(--primary-700) 0%, var(--primary-800) 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(90, 99, 230, 0.3);

    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(90, 99, 230, 0.2);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }

  svg {
    margin-left: var(--space-2);
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--space-12);
  color: var(--text-color);
  font-family: var(--font-heading-regular);
  font-size: var(--text-base);
`;

const ErrorMessage = styled.div`
  color: var(--error-600);
  text-align: center;
  padding: var(--space-4);
  background-color: var(--error-50);
  border-radius: var(--radius-lg);
  border: 1px solid var(--error-200);
  font-family: var(--font-heading-regular);
  font-size: var(--text-sm);
  font-weight: 500;
`;

function ProjectList({ onSelectProject, onCreateProject, userId }) {
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProjects = async () => {
      if (!userId) {
        setError("No user logged in");
        return;
      }
      try {
        const fetchedProjects = await getProjects(userId);
        setProjects(fetchedProjects);
        setError(null);
      } catch (error) {
        console.error('Error fetching projects:', error);
        if (error.code === 'permission-denied') {
          setError("You don't have permission to access these projects. Please check your account settings.");
        } else {
          setError("An error occurred while fetching projects. Please try again later.");
        }
      }
    };

    fetchProjects();
  }, [userId]);

  const handleProjectClick = (project) => {
    onSelectProject(project);
  };

  if (error) {
    return <ErrorMessage>{error}</ErrorMessage>;
  }

  return (
    <ProjectListContainer>
      {projects.length === 0 ? (
        <div>No projects found. Create a new project to get started!</div>
      ) : (
        projects.map(project => (
          <ProjectItem 
            key={project.id} 
            isActive={false}
            onClick={() => handleProjectClick(project)}
          >
            <ProjectName>{project.name}</ProjectName>
            <div>
              <IconButton onClick={(e) => { e.stopPropagation(); onSelectProject(project); }}>
                <FontAwesomeIcon icon={faEdit} />
              </IconButton>
              <IconButton onClick={(e) => { e.stopPropagation(); onSelectProject(project); }}>
                <FontAwesomeIcon icon={faTrash} />
              </IconButton>
            </div>
          </ProjectItem>
        ))
      )}
      <NewProjectButton onClick={onCreateProject}>
        <FontAwesomeIcon icon={faPlus} style={{ marginRight: '0.5rem' }} />
        Create New Project
      </NewProjectButton>
    </ProjectListContainer>
  );
}

export default ProjectList;